import apiClient from '@/lib/api-client';
import { homePathForRole } from '@/contexts/role-home';
import { isNative } from '@/platform/native';
import type { AuthResponse, ImpersonationState, LoginCredentials, User } from '@/types/auth';

class AuthService {
  async googleEnabled(): Promise<boolean> {
    const { data } = await apiClient.get<{ data: { enabled: boolean } }>('/v1/auth/google/config');
    return data.data.enabled;
  }

  /**
   * The Google consent URL. The server keeps the flow (and the new business's timezone and locale) in the
   * encrypted OAuth state and binds the callback to this browser with a nonce cookie.
   */
  async startGoogle(intent: 'login' | 'signup', context: { timezone?: string; locale?: string; offer?: string } = {}): Promise<string> {
    const { data } = await apiClient.post<{ data: { auth_url: string } }>('/v1/auth/google/authorize', {
      flow: intent === 'signup' ? 'register' : 'login',
      ...context,
    });
    return data.data.auth_url;
  }

  async linkGoogle(): Promise<string> {
    const { data } = await apiClient.post<{ data: { auth_url: string } }>('/v1/auth/google/link/start');
    return data.data.auth_url;
  }

  /**
   * Trades the one-time code from the callback fragment (`#code=...`) for tokens. A new sign-up goes to the
   * business details step (the same page the password sign-in opens for a business that has not filled them in), everyone else to their
   * role's home.
   */
  async exchangeGoogle(code: string): Promise<{ auth: AuthResponse; nextPath: string }> {
    const { data } = await apiClient.post<any>('/v1/auth/google/exchange', { code });
    const auth = this.transformAuthResponse(data.data);
    const nextPath = data.data.new_user ? '/admin/onboarding' : homePathForRole(auth.user.role);
    return { auth, nextPath };
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // The app signs in as a device session (403 mobile_role_not_allowed for a customer)
    if (isNative()) {
      const { deviceInfo } = await import('@/lib/mobile-auth');
      const { data } = await apiClient.post<any>('/v1/mobile/sessions', { ...credentials, device: await deviceInfo() });
      return this.transformAuthResponse(data.data);
    }
    const { data } = await apiClient.post<any>('/v1/auth/login', credentials);
    return this.transformAuthResponse(data.data);
  }

  private transformAuthResponse(response: any): AuthResponse {
    // Add organization_id to user if not present but organization is
    if (response.user && response.organization && !response.user.organization_id) {
      response.user.organization_id = response.organization.id;
    }

    return {
      user: this.transformUser(response.user),
      accessToken: response.access_token,
      refreshToken: response.refresh_token
    };
  }

  private transformUser(apiUser: any): User {
    return {
      id: apiUser.id,
      email: apiUser.email,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      phone: apiUser.phone,
      role: apiUser.role,
      organizationId: apiUser.organization_id || 0,
      active: apiUser.active,
      pendingEmail: apiUser.pending_email,
      emailConfirmed: apiUser.email_confirmed || false,
      emailConfirmedAt: apiUser.email_confirmed_at,
      tourCompletedAt: apiUser.tour_completed_at,
      hasProviderProfile: apiUser.has_provider_profile ?? false,
      createdAt: apiUser.created_at,
      updatedAt: apiUser.updated_at
    };
  }

  async logout(): Promise<void> {
    // the app revokes its device session (idempotent server side)
    if (isNative()) {
      await apiClient.delete('/v1/mobile/sessions/current', { timeout: 10_000 });
      return;
    }
    await apiClient.post('/v1/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/v1/auth/refresh', { refreshToken });
    return data;
  }

  async getCurrentUser(): Promise<User> {
    const { data } = await apiClient.get<{ data: any }>('/v1/auth/me');
    // Backend returns {data: {user: {...}, organization: {...}}, meta: {...}}
    // We need to extract just the user object
    return this.transformUser(data.data.user);
  }

  // Like getCurrentUser but also returns authoritative impersonation state.
  async getSession(): Promise<{ user: User; impersonation: ImpersonationState | null; flags?: Record<string, boolean> }> {
    const { data } = await apiClient.get<{ data: any }>('/v1/auth/me');
    const imp = data.data.impersonation;
    const flags = data.data.flags;
    return {
      user: this.transformUser(data.data.user),
      // this person's feature flags, the same answer as GET /v1/flags (older servers do not send them)
      flags: flags && typeof flags === 'object' && !Array.isArray(flags) ? flags : undefined,
      impersonation: imp
        ? { impersonatorEmail: imp.impersonator_email, sessionId: imp.session_id }
        : null,
    };
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const { data } = await apiClient.put<{ data: User }>('/v1/auth/profile', updates);
    return data.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.put('/v1/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }
}

export const authService = new AuthService();
